import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar, { NAV_GROUPS } from './Sidebar';
import TopBar from './TopBar';
import './shell.css';

/* route -> { title, group } built from the same list the rail renders, so a
   destination can never be renamed in one place and not the other. */
const ROUTE_TITLES = (() => {
  const map = {};
  NAV_GROUPS.forEach((group) => {
    group.items.forEach((item) => {
      map[item.to] = { title: item.text, group: group.label || '' };
    });
  });
  return map;
})();

function lookup(pathname) {
  const path = String(pathname || '').toLowerCase();
  if (ROUTE_TITLES[path]) return ROUTE_TITLES[path];

  /* /challenges/17 and friends still belong to /challenges. */
  const keys = Object.keys(ROUTE_TITLES);
  for (let i = 0; i < keys.length; i += 1) {
    if (path.indexOf(keys[i] + '/') === 0) return ROUTE_TITLES[keys[i]];
  }
  return null;
}

/**
 * The authenticated application frame: left rail + sticky top bar + canvas.
 *
 * Above 860px the rail is a sticky column. Below it, ui.css turns the rail into a
 * horizontal strip and shell.css turns that strip into a disclosure drawer whose
 * open/closed state lives here, so both the rail's own button and the top bar's
 * hamburger drive the same thing.
 *
 * `title` / `eyebrow` are optional: a screen may pass its own, otherwise the shell
 * names the page from the route.
 */
export default function AppShell({ title, eyebrow, children }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const here = useMemo(() => lookup(location.pathname), [location.pathname]);

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <div className="shell">
      <Sidebar open={menuOpen} onToggle={toggleMenu} onNavigate={closeMenu} />

      <div className="main">
        <TopBar
          title={title || (here ? here.title : 'Dashboard')}
          eyebrow={eyebrow || (here ? here.group : '')}
          onMenu={toggleMenu}
          menuOpen={menuOpen}
        />
        <main className="canvas">{children}</main>
      </div>
    </div>
  );
}
